import { useRef } from 'react';
import { PlusIcon, CloseIcon } from 'components/icons';

const fileToDataUri = (file) =>
  new Promise((resolve) => {
    const reader = new FileReader();
    reader.onload = () => resolve(typeof reader.result === 'string' ? reader.result : null);
    reader.onerror = () => resolve(null);
    reader.readAsDataURL(file);
  });

/**
 * Manage a list of images (existing URLs and/or newly uploaded base64 data
 * URIs). `values` is a string array; `onChange` receives the updated array.
 */
export function MultiImageUploadField({ label, values = [], onChange, hint, primary = false }) {
  const inputRef = useRef(null);

  const handleFiles = async (event) => {
    const files = Array.from(event.target.files || []);
    event.target.value = ''; // allow re-picking the same files
    if (files.length === 0) return;
    const uris = (await Promise.all(files.map(fileToDataUri))).filter(Boolean);
    if (uris.length > 0) onChange([...values, ...uris]);
  };

  const removeAt = (index) => onChange(values.filter((_, i) => i !== index));

  // Move an image to the front (making it the primary photo).
  const makePrimary = (index) =>
    onChange([values[index], ...values.filter((_, i) => i !== index)]);

  return (
    <div>
      {label && <span className="mb-1.5 block text-sm font-medium text-slate-700">{label}</span>}
      <div className="flex flex-wrap gap-2">
        {values.map((src, index) => (
          <div
            key={`${index}-${src.slice(0, 24)}`}
            className="group relative h-24 w-24 overflow-hidden rounded-lg border border-surface-200 bg-surface-100"
          >
            <img
              src={src}
              alt=""
              className="h-full w-full object-cover"
              onError={(event) => {
                event.currentTarget.style.display = 'none';
              }}
            />
            {primary && index === 0 && (
              <span className="absolute left-1 top-1 rounded bg-black/60 px-1.5 py-0.5 text-[10px] font-medium text-white">
                Primary
              </span>
            )}
            {primary && index !== 0 && (
              <button
                type="button"
                onClick={() => makePrimary(index)}
                className="absolute inset-x-1 bottom-1 rounded bg-black/60 py-0.5 text-[10px] font-medium text-white opacity-0 transition group-hover:opacity-100"
              >
                Make primary
              </button>
            )}
            <button
              type="button"
              onClick={() => removeAt(index)}
              aria-label="Remove image"
              className="absolute right-0.5 top-0.5 flex h-5 w-5 items-center justify-center rounded-full bg-slate-900/60 text-white transition hover:bg-slate-900/80"
            >
              <CloseIcon className="h-3 w-3" />
            </button>
          </div>
        ))}

        <button
          type="button"
          onClick={() => inputRef.current?.click()}
          className="flex h-24 w-24 flex-col items-center justify-center gap-1 rounded-lg border border-dashed border-surface-300 text-slate-400 transition hover:border-brand-300 hover:text-brand-600"
        >
          <PlusIcon className="h-5 w-5" />
          <span className="text-[10px] font-medium">Add</span>
        </button>
      </div>
      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        multiple
        onChange={handleFiles}
        className="hidden"
      />
      {hint && <p className="mt-1.5 text-xs text-slate-500">{hint}</p>}
    </div>
  );
}

export default MultiImageUploadField;
